import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { MandatoPDF } from '../components/MandatoPDF';
import { BonificaPDF } from '../components/BonificaPDF';
import { ConsulenzaPDF } from '../components/ConsulenzaPDF';
import { DatiSoggettoPDF } from '../components/DatiSoggettoPDF';
import { InformativaPrivacyPDF } from '../components/InformativaPrivacyPDF';
import { appendPrivacyPolicy, immagineInPng, resolvePrivacyBlob } from './privacyPdf';

/**
 * Moduli in bianco della cartella "Doc. Ufficio" del file manager: gli stessi documenti del
 * mandato, con i dati dell'agenzia e senza quelli di cliente e soggetto, da stampare e
 * compilare a penna. Generati al momento, non salvati.
 */
export type ModuloInBianco = 'datiSoggetto' | 'informativa' | 'mandatoInvestigativo' | 'mandatoBonifica' | 'mandatoConsulenza';

export const MODULI_IN_BIANCO: { tipo: ModuloInBianco; nome: string; file: string }[] = [
    { tipo: 'datiSoggetto', nome: 'Dati soggetto investigato', file: 'Dati_soggetto_investigato.pdf' },
    { tipo: 'informativa', nome: 'Informativa privacy', file: 'Informativa_privacy.pdf' },
    { tipo: 'mandatoInvestigativo', nome: 'Mandato investigativo con informativa', file: 'Mandato_investigativo.pdf' },
    { tipo: 'mandatoBonifica', nome: 'Mandato di bonifica con informativa', file: 'Mandato_bonifica.pdf' },
    { tipo: 'mandatoConsulenza', nome: 'Mandato di consulenza con informativa', file: 'Mandato_consulenza.pdf' },
];

/** Impostazioni con le immagini in PNG base64, come le usa il mandato. */
export const impostazioniPerPdf = async (settings: any): Promise<any> => {
    const [logo, header, footer, mandatoHeader] = await Promise.all([
        immagineInPng(settings?.logoUrl),
        immagineInPng(settings?.reportHeaderUrl),
        immagineInPng(settings?.reportFooterUrl),
        immagineInPng(settings?.mandatoHeaderUrl),
    ]);
    return {
        ...settings,
        logoUrl: logo || settings?.logoUrl,
        reportHeaderUrl: header || settings?.reportHeaderUrl,
        reportFooterUrl: footer || settings?.reportFooterUrl,
        mandatoHeaderUrl: mandatoHeader || settings?.mandatoHeaderUrl,
        mandatoFooterUrl: '',
    };
};

/** Dati del mandato senza cliente né soggetto; `inBianco` toglie le scelte predefinite. */
export const datiInBianco = (settings: any) => ({
    cliente: {},
    pratica: {},
    delegati: [],
    logoUrl: settings?.logoUrl || '',
    nome_azienda: settings?.agencyName || '',
    num_ore: '',
    rimborso_km: '',
    apparati_localizzazione: '',
    cauzione: 0,
    acconto_compenso: 0,
    localizzazione_satellitare: false,
    ripresa: false,
    testo_altro_apparati: '',
    investigatori_esterni: '',
    nomina_responsabile: false,
    settings,
    inBianco: true,
});

/** PDF del modulo; `settingsPdf` = risultato di {@link impostazioniPerPdf}. */
export const generaModuloInBianco = async (tipo: ModuloInBianco, settingsPdf: any): Promise<Blob> => {
    const data = datiInBianco(settingsPdf);
    if (tipo === 'datiSoggetto') return pdf(<DatiSoggettoPDF data={data} />).toBlob();
    if (tipo === 'informativa') {
        const privacy = await resolvePrivacyBlob(settingsPdf);
        return privacy ? privacy.blob : pdf(<InformativaPrivacyPDF settings={settingsPdf} />).toBlob();
    }
    const mandato = tipo === 'mandatoBonifica' ? <BonificaPDF data={data} />
        : tipo === 'mandatoConsulenza' ? <ConsulenzaPDF data={data} />
        : <MandatoPDF data={data} />;
    const blob = await pdf(mandato).toBlob();
    const privacy = await resolvePrivacyBlob(settingsPdf);
    return privacy ? appendPrivacyPolicy(blob, privacy.blob, privacy.source) : blob;
};
